import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody } from '@/components/ui/table';
import { useSorting } from '@/hooks/useSorting';
import { useAdminActions } from '@/hooks/useAdminActions';
import WorkBehaviorTableHeader from '@/components/admin/WorkBehaviorTableHeader';
import EmptyStateMessage from '@/components/admin/EmptyStateMessage';
import IdealProfileSelector from '@/components/IdealProfileSelector';
import AdminSearchBar from '@/components/AdminSearchBar';
import { Button } from '@/components/ui/button';
import { Filter, Loader2 } from 'lucide-react';
import AdminPaginationControls from '@/components/AdminPaginationControls';
import WorkBehaviorTableRow from '@/components/WorkBehaviorTableRow';
import { IdealProfile } from '@/types/dashboardTypes';
import { useBehavioralSubmissionData } from '@/hooks/useBehavioralSubmissionData';
import { CandidateTableSkeleton } from '@/components/skeletons/TableSkeleton';
import ProfileModal from '@/components/ProfileModal';
import type { FilterState } from '@/types/filterTypes';
interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words: any;
  selected_words_text: any;
  completed_at: string;
  created_at: string;
  company_id?: string | null;
  status?: string;
}
interface AdminWorkBehaviorTabProps {
  loading?: boolean;
  filteredSubmissions: SurveySubmission[];
  allSubmissions: SurveySubmission[];
  currentFilters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  filtersExpanded: boolean;
  onToggleFilters: () => void;
  onViewDetails: (submission: SurveySubmission) => void;
  onSubmissionDeleted: (id: string) => void;
  onStatusUpdate?: (submissionId: string, newStatus: string) => Promise<void>;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    setCurrentPage: (page: number) => void;
    setPageSize: (size: number) => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    resetToFirstPage: () => void;
  };
  totalCount: number;
  searchTerm: string;
  isSearching?: boolean;
  onSearchChange: (term: string) => void;
  tableExpanded: boolean;
  onToggleTable: () => void;
  selectedIdealProfile: IdealProfile | null;
  setSelectedIdealProfile: (profile: IdealProfile | null) => void;
}
const AdminWorkBehaviorTab: React.FC<AdminWorkBehaviorTabProps> = ({
  loading = false,
  filteredSubmissions,
  onViewDetails,
  onSubmissionDeleted,
  onStatusUpdate,
  pagination,
  totalCount,
  tableExpanded,
  onToggleTable,
  selectedIdealProfile,
  filtersExpanded,
  onToggleFilters,
  setSelectedIdealProfile,
  searchTerm,
  isSearching = false,
  onSearchChange
}) => {
  // Local state for optimistic updates
  const [optimisticSubmissions, setOptimisticSubmissions] = useState<SurveySubmission[]>([]);
  const [deletingSubmissions, setDeletingSubmissions] = useState<Set<string>>(new Set());
  
  // Local state for candidate modal
  const [selectedCandidate, setSelectedCandidate] = useState<SurveySubmission | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle view details - open the modal
  const handleViewDetails = (submission: SurveySubmission) => {
    setSelectedCandidate(submission);
    setIsModalOpen(true);
  };

  // Merge optimistic updates with actual data
  const mergedSubmissions = useMemo(() => {
    const optimisticMap = new Map(optimisticSubmissions.map(sub => [sub.id, sub]));
    return filteredSubmissions
      .filter(sub => !deletingSubmissions.has(sub.id))
      .map(sub => optimisticMap.get(sub.id) || sub);
  }, [filteredSubmissions, optimisticSubmissions, deletingSubmissions]);

  const {
    enhancedSubmissions,
    isSuperAdmin
  } = useBehavioralSubmissionData(mergedSubmissions, selectedIdealProfile);
  
  const {
    sortedData,
    sortConfig,
    handleSort
  } = useSorting({
    data: enhancedSubmissions,
    defaultSortKey: 'completed_at',
    defaultSortDirection: 'desc'
  });
  
  const {
    handleSubmissionDelete
  } = useAdminActions();

  // Optimistic status update
  const handleOptimisticStatusUpdate = async (submissionId: string, newStatus: string) => {
    const targetSubmission = filteredSubmissions.find(sub => sub.id === submissionId);
    if (!targetSubmission) return;

    const previousStatus = targetSubmission.status;
    
    // Optimistic update
    setOptimisticSubmissions(prev => {
      const filtered = prev.filter(sub => sub.id !== submissionId);
      return [...filtered, { ...targetSubmission, status: newStatus }];
    });

    try {
      if (onStatusUpdate) {
        await onStatusUpdate(submissionId, newStatus);
      }
      
      // Remove from optimistic state after successful update
      setOptimisticSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
    } catch (error) {
      // Rollback on error
      setOptimisticSubmissions(prev => {
        const filtered = prev.filter(sub => sub.id !== submissionId);
        return [...filtered, { ...targetSubmission, status: previousStatus }];
      });
      throw error;
    }
  };

  // Optimistic deletion - this is the single source of truth for delete operations
  const handleOptimisticDelete = async (submissionId: string) => {
    const submission = filteredSubmissions.find(s => s.id === submissionId);
    
    // Mark as deleting immediately
    setDeletingSubmissions(prev => new Set([...prev, submissionId]));

    try {
      const success = await handleSubmissionDelete(
        submissionId, 
        () => {
          // After successful delete, notify parent to refresh data
          onSubmissionDeleted(submissionId);
        },
        false, // not cognitive-only
        submission?.email // pass email for proper unlinking
      );
      
      if (success) {
        // Remove from optimistic state on success
        setOptimisticSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
      }
      return success;
    } catch (error) {
      // Restore on error
      setDeletingSubmissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
      throw error;
    } finally {
      // Always remove from deleting state
      setDeletingSubmissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  };
  if (loading) {
    return (
      <div className="space-y-4">
        <CandidateTableSkeleton />
      </div>
    );
  }

  return <div className="space-y-4">
      {/* Filters Section */}
      <Card className="border-border bg-card">
        
      </Card>

      {/* Table Section */}
      <div className="border border-border rounded-lg bg-card overflow-hidden">
        {isSearching ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <WorkBehaviorTableHeader sortConfig={sortConfig} onSort={handleSort} isSuperAdmin={isSuperAdmin} selectedIdealProfile={selectedIdealProfile} tableExpanded={tableExpanded} />
                  <TableBody>
                    {sortedData.map(submission => (
                      <WorkBehaviorTableRow 
                        key={submission.id} 
                        submission={submission} 
                        onViewDetails={handleViewDetails} 
                        onSubmissionDeleted={handleOptimisticDelete} 
                        onStatusUpdate={handleOptimisticStatusUpdate} 
                        hideProfile={true} 
                        selectedIdealProfile={selectedIdealProfile} 
                        cognitivePercentile={submission.cognitivePercentile} 
                        isExpanded={tableExpanded} 
                        showCompany={isSuperAdmin} 
                        companyName={submission.companyName} 
                        showChart={true}
                      />
                    ))}
                </TableBody>
              </Table>
            </div>
            
            {!loading && filteredSubmissions.length === 0 && <div className="py-12 text-center">
                <EmptyStateMessage />
              </div>}
          </>
        )}
        
        {totalCount > 0 && <div className="border-t border-border px-4 py-3 bg-muted/20">
            <AdminPaginationControls currentPage={pagination.currentPage} totalPages={pagination.totalPages} pageSize={pagination.pageSize} totalItems={totalCount} startIndex={pagination.startIndex} endIndex={pagination.endIndex} hasNextPage={pagination.hasNextPage} hasPreviousPage={pagination.hasPreviousPage} onPageChange={pagination.setCurrentPage} onPageSizeChange={pagination.setPageSize} onNextPage={pagination.goToNextPage} onPreviousPage={pagination.goToPreviousPage} />
          </div>}
      </div>
      
      {/* Profile Analysis Modal */}
      <ProfileModal
        submission={selectedCandidate}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCandidate(null);
        }}
      />
    </div>;
};
export default AdminWorkBehaviorTab;